import { exposed, fromNode, ViewModel } from '@rehaje/core'
import { reactive, toJS } from '@rehaje/reactive'
import { createURL, fromEntries } from '@rehaje/shared'

import type { CSSStyleSheetModel } from '../CSSStyleSheet/ViewModel'
import type { QueryModel } from '../Query/ViewModel'
import type { VariableModel } from '../Variable/ViewModel'
import type { PageNode } from './TypedNode'
import type { TRecord } from '@rehaje/shared'
import type {
  Location,
  NavigateFunction,
  Params,
  SetURLSearchParams,
  URLSearchParamsInit
} from 'react-router-dom'

export type PageOpenWay = 'currentTab' | 'newTab'
export interface RouteState {
  location: Location
  navigate: NavigateFunction
  params: Readonly<Params<string>>
  setURLSearchParams: SetURLSearchParams
  urlSearchParams: URLSearchParams
}

export class PageModel extends ViewModel {
  @reactive()
  __r_route_state?: RouteState

  @reactive()
  __r_url_params?: TRecord

  @fromNode()
  @reactive()
  index?: boolean

  @fromNode()
  @reactive()
  path?: string = ''

  @fromNode()
  @reactive('shallow')
  queries: QueryModel[] = []

  @fromNode()
  @reactive('shallow')
  stylesheets: CSSStyleSheetModel[] = []

  @fromNode()
  @reactive('shallow')
  variables: VariableModel[] = []

  @exposed()
  get basename() {
    return this.__r_getDefaultBaseName()
  }

  @exposed()
  get location() {
    return this.__r_route_state?.location ?? globalThis.location
  }

  @exposed()
  get routeParams() {
    return this.__r_route_state?.params ?? {}
  }

  @exposed()
  get searchParams() {
    return (
      this.__r_route_state?.urlSearchParams ??
      new URLSearchParams(globalThis.location.search)
    )
  }

  @exposed()
  get urlParams() {
    return toJS(this.__r_url_params) ?? this.__r_getUrlParams()
  }

  constructor(node: PageNode) {
    super(node)
    this.addRenderEffect(() => {
      const onHashChange = () => {
        this.__r_url_params = this.__r_getUrlParams()
      }
      globalThis.addEventListener('hashchange', onHashChange)
      return () => {
        globalThis.removeEventListener('hashchange', onHashChange)
      }
    })
  }

  __r_getDefaultBaseName() {
    const currentPathName = globalThis.location.pathname
    if (this.__r_route_state) {
      const pathname = this.__r_route_state?.location.pathname
      const pathnameIndex = currentPathName.indexOf(pathname)
      return `${currentPathName.slice(0, pathnameIndex)}`
    }
    return ''
  }

  __r_getUrlParams() {
    return {
      ...fromEntries(new URLSearchParams(globalThis.location.search)),
      hash: fromEntries(
        new URLSearchParams(globalThis.location.hash.replace(/^(#|%23)/, ''))
      ),
      href: globalThis.location.href
    }
  }

  navigate(
    url?: string,
    params?: TRecord,
    openWay: PageOpenWay = 'currentTab'
  ) {
    const absolute = openWay !== 'currentTab'
    const newUrl = createURL(url, params, absolute, this.basename)
    switch (openWay) {
      case 'currentTab': {
        return this.__r_route_state?.navigate(newUrl)
      }
      case 'newTab': {
        globalThis.open(newUrl, '_blank')
        break
      }
    }
  }

  redirect(url?: string, params?: TRecord, openWay?: PageOpenWay) {
    this.navigate(url, params, openWay)
  }

  register(state?: RouteState) {
    if (state) this.__r_route_state = state
    this.evaluate()
  }

  setSearchParams(params: URLSearchParamsInit) {
    this.__r_route_state?.setURLSearchParams(params)
  }
}
